import React, { useEffect, useState } from "react";
import { Autocomplete, FormHelperText, TextField } from "@mui/material";
import { FormikProps } from "formik";

import Grid2 from "@mui/material/Grid2";
import { TypeUser } from "src/interface/enum/TypeUser";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/utilities";
import { BaseDefinitionType } from "src/interface/enum/BaseDefinitionType ";
import { useAuth } from "src/context/auth";
import { IBaseDefinition } from "src/interface/IBaseDefinition";
import { IAppUser } from "src/interface/IUser";
import { ListOfStatusGlobal } from "src/interface/enum/StatusGlobal";
import { DatePicker } from "@mui/x-date-pickers";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";

const TaskForm = ({ formik }: { formik: FormikProps<any> }) => {
  const { user } = useAuth();
  const { data: TaskTitleData } = useQuery({
    queryKey: ["TaskTitleData"],
    queryFn: async () => {
      const response = await fetch(
        API_URL + "base-definition/type/" + BaseDefinitionType.TaskTitle,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`خطا در دریافت اطلاعات: ${response.status}`);
      }

      const text = await response.text();
      if (!text) {
        return [];
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        console.error("خطا در پردازش JSON:", text);
        return [];
      }
    },
    enabled: !!user?.token,
  });
  const [getTitles, setTitles] = React.useState<IBaseDefinition[]>([]);
  React.useEffect(() => {
    if (TaskTitleData) {
      setTitles(
        TaskTitleData.map((item: IBaseDefinition) => ({
          label: item.caption,
          value: item.id,
        }))
      );
    }
  }, [TaskTitleData]);

  const { data: CustomersData } = useQuery({
    queryKey: ["CustomersData"],
    queryFn: async () => {
      const response = await fetch(
        API_URL + "user/getByRole/" + TypeUser.CUSTOMER,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`خطا در دریافت اطلاعات: ${response.status}`);
      }

      const text = await response.text();
      if (!text) {
        return [];
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        console.error("خطا در پردازش JSON:", text);
        return [];
      }
    },
    enabled: !!user?.token,
  });
  const [getCustomers, setCustomers] = useState<IAutoCompleteLabelValue[]>([]);
  useEffect(() => {
    if (CustomersData) {
      setCustomers(
        CustomersData.filter((c: IAppUser) => c.parentRef === null).map(
          (item: IAppUser) => {
            // ساخت label با ترکیب نام و نام شرکت
            const namePart = `${item.fName ?? ""} ${item.lName ?? ""}`.trim();
            const coNamePart = item.coName ?? "";
            const label =
              namePart && coNamePart
                ? `${namePart} - ${coNamePart}`
                : namePart || coNamePart || item.userName || item.id || "";

            return {
              label: label,
              value: item.id,
              item: item,
            };
          }
        )
      );
    }
  }, [CustomersData]);

  const { data: EditorsData } = useQuery({
    queryKey: ["EditorsData"],
    queryFn: async () => {
      const response = await fetch(
        API_URL + "user/getByRole/" + TypeUser.EDITOR,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`خطا در دریافت اطلاعات: ${response.status}`);
      }

      const text = await response.text();
      if (!text) {
        return [];
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        console.error("خطا در پردازش JSON:", text);
        return [];
      }
    },
    enabled: !!user?.token,
  });
  const [getEditors, setEditors] = useState<IAutoCompleteLabelValue[]>([]);
  console.log("🚀 ~ TaskForm ~ getEditors:", getEditors);
  useEffect(() => {
    if (EditorsData) {
      setEditors(
        EditorsData.map((item: IAppUser) => ({
          label: item.fName + " " + item.lName,
          value: item.id,
          item: item,
        }))
      );

      // اگر userRef خالی باشد، آن را به کاربر فعلی تنظیم کن
      if (!formik.values.userRef && user?.userName) {
        const currentUser = EditorsData.find(
          (editor: IAppUser) => editor.userName === user.userName
        );
        if (currentUser) {
          formik.setFieldValue("userRef", currentUser.id);
        }
      }
    }
  }, [EditorsData, formik, user?.userName]);

  console.log("user", user?.userName);

  return (
    <>
      <Grid2 container spacing={1} marginY={1} columns={13}>
        <Grid2 size={3}>
          <DatePicker
            label="تاریخ ایحاد"
            onChange={(date: Date | null) =>
              formik.setFieldValue("createdOn", date)
            }
            disabled
            readOnly
            //@ts-ignore
            value={formik.values.createdOn || new Date()}
            defaultValue={new Date()}
          />
          <FormHelperText
            error={formik.touched.createdOn && Boolean(formik.errors.createdOn)}
          >
            {(formik.touched.createdOn && formik.errors.createdOn) as boolean}
          </FormHelperText>
        </Grid2>
        <Grid2 size={2}>
          <TextField
            {...formik.getFieldProps("priority")} // استفاده از ویژگی‌های فرمیک
            label="اولویت"
            type="number"
            error={formik.touched.priority && Boolean(formik.errors.priority)}
            helperText={
              (formik.touched.priority && formik.errors.priority) as boolean
            }
            variant="outlined"
            fullWidth
          />
        </Grid2>

        <Grid2 size={4}>
          <Autocomplete
            value={formik.values.titleTask}
            freeSolo
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue("titleTask", newValue?.label || undefined);
            }}
            options={getTitles || []}
            fullWidth
            renderInput={(params) => (
              <TextField
                onChange={(e) => {
                  formik.setFieldValue(
                    "titleTask",
                    e.target.value || undefined
                  );
                }}
                error={
                  formik.touched.titleTask && Boolean(formik.errors.titleTask)
                }
                helperText={
                  (formik.touched.titleTask &&
                    formik.errors.titleTask) as boolean
                }
                {...params}
                label="عنوان"
                placeholder="عنوان"
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Autocomplete
            value={
              ListOfStatusGlobal.find(
                (s) => s.value === formik.values.statusTask
              ) || null
            }
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue("statusTask", newValue?.value || undefined);
            }}
            options={ListOfStatusGlobal}
            fullWidth
            renderInput={(params) => (
              <TextField
                error={
                  formik.touched.statusTask && Boolean(formik.errors.statusTask)
                }
                helperText={
                  (formik.touched.statusTask &&
                    formik.errors.statusTask) as boolean
                }
                {...params}
                label="وضعیت"
                placeholder="وضعیت"
              />
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2 container spacing={1} marginBottom={1} columns={13}>
        <Grid2 size={3}>
          <DatePicker
            label="تاریخ سر رسید"
            onChange={(date: Date | null) =>
              formik.setFieldValue("dueDate", date)
            }
            //@ts-ignore
            value={formik.values.dueDate || new Date()}
            defaultValue={new Date()}
          />
          <FormHelperText
            error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
          >
            {(formik.touched.dueDate && formik.errors.dueDate) as boolean}
          </FormHelperText>
        </Grid2>

        <Grid2 size={5}>
          <Autocomplete
            value={
              getCustomers.find(
                (customer: any) => customer.value === formik.values.customerRef
              ) || null
            }
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue("customerRef", newValue?.value || undefined);
            }}
            options={getCustomers || []}
            getOptionLabel={(option: any) => option.label || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              option.value === value?.value
            }
            renderOption={(props: any, option: any) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            fullWidth
            renderInput={(params) => (
              <TextField
                error={
                  formik.touched.customerRef &&
                  Boolean(formik.errors.customerRef)
                }
                helperText={
                  (formik.touched.customerRef &&
                    formik.errors.customerRef) as boolean
                }
                {...params}
                label="مشتری"
                placeholder="مشتری"
              />
            )}
          />
        </Grid2>

        <Grid2 size={5}>
          <Autocomplete
            value={
              getEditors.find(
                (editor: any) => editor.value === formik.values.userRef
              ) || null
            }
            onChange={(event: any, newValue: any) => {
              formik.setFieldValue("userRef", newValue?.value || undefined);
            }}
            options={
              getEditors.filter(
                (editor: IAutoCompleteLabelValue) => editor.item.isActive
              ) || []
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                error={formik.touched.userRef && Boolean(formik.errors.userRef)}
                helperText={
                  (formik.touched.userRef && formik.errors.userRef) as boolean
                }
                {...params}
                label="کارمند مرتبط"
                placeholder="کارمند مرتبط"
              />
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2 container spacing={1} marginBottom={1}>
        <Grid2 size={12}>
          <TextField
            {...formik.getFieldProps("desc")} // استفاده از ویژگی‌های فرمیک
            label="توضیحات"
            multiline
            rows={3}
            placeholder="توضیحات"
            error={formik.touched.desc && Boolean(formik.errors.desc)}
            helperText={(formik.touched.desc && formik.errors.desc) as boolean}
            variant="outlined"
            fullWidth
          />
        </Grid2>
      </Grid2>
    </>
  );
};

export default TaskForm;
